import { beastModeProfilePageRevamp } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { List, Map } from '@gonfalon/immutable-shim/immutableShim';
import { fromJS } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import {
  beastModeError,
  beastModeRefresh,
  http,
  jsonToImmutable,
  jsonToImmutableError,
  middleware,
  restApitoImmutableError,
} from '@gonfalon/legacy-http-client/httpUtils';
import { ApiVersions } from '@gonfalon/openapi/api-versions';
import type { GetMemberQueryParams } from '@gonfalon/openapi/operations/getMember';
import type { RoleName } from '@gonfalon/permissions/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { fetchMember } from '@gonfalon/rest-api/fetchers/fetchMember';
import { getProfileQuery } from '@gonfalon/rest-api/queries/members';
import { resolveAttributionReferer } from '@gonfalon/signup/agentUtmParams';

import type { Account } from '#utils/accounts/records/Account';
import { createAccount as createAccountRecord } from '#utils/accounts/records/Account';
import type { RequestSeatsFormRecord } from '#utils/accounts/records/BulkInvites';
import type { FacilitatedTrial, FacilitatedTrialSignUpForm } from '#utils/accounts/records/FacilitatedTrial';
import { createFacilitatedTrial } from '#utils/accounts/records/FacilitatedTrial';
import type { FollowPreferences } from '#utils/accounts/records/Follow';
import { createFollowPreferences } from '#utils/accounts/records/Follow';
import type { InvitationDetails, NewJoinTeamRequest, PasswordUpdate } from '#utils/accounts/records/Invitations';
import type {
  ImmutableNotificationsSettingsType,
  NotificationChannel,
  NotificationsSettingsType,
  NotificationTopics,
} from '#utils/accounts/records/Notifications';
import type { OwnerInfo } from '#utils/accounts/records/Owner';
import type { SamlAppDetails, SamlConfig } from '#utils/accounts/records/SamlConfig';
import { createSamlConfig as createSamlConfigRecord } from '#utils/accounts/records/SamlConfig';
import type { ScimConfig } from '#utils/accounts/records/ScimConfig';
import { createScimConfig } from '#utils/accounts/records/ScimConfig';
import type { MFARecoveryCodes } from '#utils/accounts/records/Security';
import { createMFARecoveryCodes } from '#utils/accounts/records/Security';
import type { SignupForm } from '#utils/accounts/records/SignupForm';
import { Logger } from '#utils/logUtils';
import { getBulkAddMembersToTeamsSemanticPatchInstructions } from '#utils/members/getBulkAddMembersToTeamsSemanticPatchInstructions';
import { getBulkEditMemberRolesSemanticPatchInstructions } from '#utils/members/getBulkEditMemberRolesSemanticPatchInstructions';
import type { Member } from '#utils/members/records/Member';
import { createMember } from '#utils/members/records/Member';
import type { MemberFilters } from '#utils/memberUtils';
import { createJsonPatch } from '#utils/patchUtils';
import { clearStoredSignupLandingUrl, readStoredCustomReferer } from '#utils/signupLandingUrlStorage';
import type { SignupOutcome } from '#utils/signupOutcome';

const logger = Logger.get('AccountAPI');

function reportAccesses(resourceSpec: string) {
  return http
    .report('/internal/accesses', {
      body: {
        resourceSpec,
      },
    })
    .then(jsonToImmutable)
    .catch(jsonToImmutableError);
}

function getMember(memberId: string, { params }: { params?: GetMemberQueryParams } = {}): Promise<Member> {
  return fetchMember({ memberId, params })
    .then((data) => fromJS(data))
    .then(createMember)
    .catch(jsonToImmutableError);
}

function createAccountFromFacilitatedTrial(
  password: FacilitatedTrialSignUpForm,
  token: string,
): Promise<void | Response> {
  return http
    .post(`/internal/account/signup/facilitated-trial/${token}`, {
      body: JSON.stringify(password.toJS()),
    })
    .catch(jsonToImmutableError);
}

function getFacilitatedTrial(token: string): Promise<FacilitatedTrial> {
  return http
    .get(`/internal/account/signup/facilitated-trial/${token}/validate`)
    .then(jsonToImmutable)
    .then(createFacilitatedTrial)
    .catch(jsonToImmutableError);
}

type CreateUnverifiedMemberResult = {
  response: Response;
  signupOutcome?: SignupOutcome;
  redirectUrl?: string;
};

async function parseCreateUnverifiedMemberResponse(response: Response): Promise<CreateUnverifiedMemberResult> {
  const result: CreateUnverifiedMemberResult = { response };
  if (response.ok) {
    try {
      const body = (await response.clone().json()) as {
        signupOutcome?: SignupOutcome;
        redirectUrl?: string;
      };
      result.signupOutcome = body.signupOutcome;
      result.redirectUrl = body.redirectUrl;
    } catch (error) {
      logger.warn('createUnverifiedMember: malformed JSON in success response', error);
    }
  }
  return result;
}

function createUnverifiedMember(account: SignupForm): Promise<CreateUnverifiedMemberResult> {
  const payload = account.toJS();
  if (!payload.customReferer) {
    // Stash first -- it holds the first-touch landing URL. The fallback covers
    // /oauth/google, which has no URL of its own: there the server's echo of the
    // captured cookie is all the client can see. With neither, signupv2 persists the
    // bare callback page, and the verification email link loses the campaign.
    payload.customReferer = readStoredCustomReferer() || resolveAttributionReferer() || undefined;
  }
  return http
    .post('/internal/account/signupv2', { body: JSON.stringify(payload) })
    .then(parseCreateUnverifiedMemberResponse)
    .then((result) => {
      // Only on success: a rejected POST keeps the stash for the retry, which is
      // submitted from this same still-mounted form.
      clearStoredSignupLandingUrl();
      return result;
    })
    .catch(jsonToImmutableError);
}

function deleteAccount(): Promise<void | Response> {
  return http.delete('/internal/account').catch(jsonToImmutableError).then(beastModeRefresh, beastModeError);
}

function deleteDuplicateOrganization(): Promise<void | Response> {
  return http.delete('/internal/account/duplicate-organization').catch(jsonToImmutableError);
}

function updateDuplicateOrganization(): Promise<void | Response> {
  return http.patch('/internal/account/duplicate-organization').catch(jsonToImmutableError);
}

function getAccount() {
  return http.get('/internal/account').then(jsonToImmutable).then(createAccountRecord).catch(jsonToImmutableError);
}

function updateAccount(oldAccount: Account, newAccount: Account): Promise<Account> {
  const patch = createJsonPatch(oldAccount, newAccount);
  return http
    .patch(oldAccount.getIn(['_links', 'self', 'href']), {
      body: patch,
    })
    .then(jsonToImmutable)
    .then(createAccountRecord)
    .catch(jsonToImmutableError)
    .catch((error: ImmutableServerError) => beastModeError(error));
}

function updateMember(oldMember: Member, newMember: Member): Promise<Member> {
  const patch = createJsonPatch(oldMember, newMember, {
    propFilter: false,
    shouldTestVersion: newMember.version !== null,
  });
  return http
    .patch(oldMember.getIn(['_links', 'self', 'href']), {
      body: patch,
    })
    .then(jsonToImmutable)
    .then(createMember)
    .catch(jsonToImmutableError);
}

function bulkReplaceMembersRoles(
  memberIDs: string[],
  customRoleKeys: string[],
  role: RoleName,
  filter: MemberFilters,
  comment?: string,
) {
  const commentPayload = comment ? { comment } : {};
  const instructions = getBulkEditMemberRolesSemanticPatchInstructions(memberIDs, customRoleKeys, role, filter);
  return http
    .patch('/api/v2/members', {
      headers: {
        'Ld-Api-Version': ApiVersions.Beta,
        'Content-Type': 'application/json; domain-model=launchdarkly.semanticpatch',
      },
      body: { instructions, ...commentPayload },
    })
    .then((response) => middleware.json(response))
    .catch(jsonToImmutableError);
}

function bulkReplaceAllMembersRoles(
  ignoredMemberIDs: string[],
  customRoleKeys: string[],
  role: RoleName,
  filters: MemberFilters,
) {
  const instructions = getBulkEditMemberRolesSemanticPatchInstructions(
    ['*'],
    customRoleKeys,
    role,
    filters,
    ignoredMemberIDs,
  );
  return http
    .patch('/api/v2/members', {
      headers: {
        'Ld-Api-Version': ApiVersions.Beta,
        'Content-Type': 'application/json; domain-model=launchdarkly.semanticpatch',
      },
      body: { instructions },
    })
    .then((response) => middleware.json(response))
    .catch(jsonToImmutableError);
}

function addMemberToTeams(memberId: string, teamKeys: string[]): Promise<Member> {
  return http
    .post(`/api/v2/members/${memberId}/teams`, {
      body: { teamKeys },
    })
    .then(jsonToImmutable)
    .then(createMember)
    .catch(jsonToImmutableError);
}

function bulkAddMembersToTeams(memberIDs: string[], teamKeys: string[], filter: MemberFilters) {
  const instructions = getBulkAddMembersToTeamsSemanticPatchInstructions(memberIDs, teamKeys, filter);
  return http
    .patch('/api/v2/teams', {
      headers: {
        'Ld-Api-Version': ApiVersions.Beta,
        'Content-Type': 'application/json; domain-model=launchdarkly.semanticpatch',
      },
      body: { instructions },
    })
    .then((response) => middleware.json(response))
    .catch(jsonToImmutableError);
}

function bulkAddAllMembersToTeams(ignoredMemberIDs: string[], teamKeysToAdd: string[], filter: MemberFilters) {
  const instructions = getBulkAddMembersToTeamsSemanticPatchInstructions(
    ['*'],
    teamKeysToAdd,
    filter,
    ignoredMemberIDs,
  );
  return http
    .patch('/api/v2/teams', {
      headers: {
        'Ld-Api-Version': ApiVersions.Beta,
        'Content-Type': 'application/json; domain-model=launchdarkly.semanticpatch',
      },
      body: { instructions },
    })
    .then((response) => middleware.json(response))
    .catch(jsonToImmutableError);
}

function updateAccountOwner(ownerInfo: OwnerInfo) {
  return http
    .post('/internal/account/owner', {
      body: JSON.stringify({
        promotedMemberId: `${ownerInfo.promotedMember._id}`,
      }),
    })
    .catch(jsonToImmutableError)
    .then(beastModeRefresh, beastModeError);
}

function requestEnterpriseSeats(seatsRequest: RequestSeatsFormRecord) {
  return http
    .put('/internal/billingv2/account/subscription/opportunity/enterprise-seats', {
      body: JSON.stringify(seatsRequest.toJS()),
    })
    .catch(jsonToImmutableError);
}
function inviteMembers(
  invites: List<{
    email: string;
    role: string;
    customRoles: List<string>;
    roleAttributes: Map<string, List<string>>;
  }>,
): Promise<List<Member>> {
  return http
    .post('/api/v2/members', {
      body: invites,
    })
    .then(jsonToImmutable)
    .then((data) => data.get('items').map(createMember))
    .catch(jsonToImmutableError);
}

function resendInvite(member: Member): Promise<Member> {
  return http.post(member.getIn(['_links', 'resendInvitation', 'href'])).catch(jsonToImmutableError);
}

function deleteMember(member: Member): Promise<void> {
  return http.delete(member.getIn(['_links', 'self', 'href'])).catch(jsonToImmutableError);
}

function acceptInvitation(details: InvitationDetails, invitationToken: string) {
  return http
    .post(`/internal/invite/${invitationToken}`, {
      body: JSON.stringify(details.toJS()),
    })
    .catch(jsonToImmutableError);
}

function acceptInvitationMFA(token: string, passcode: string, invitationToken: string): Promise<MFARecoveryCodes> {
  return http
    .post(`/internal/invite/${invitationToken}/mfa`, {
      body: { token, passcode },
    })
    .then(jsonToImmutable)
    .then(createMFARecoveryCodes)
    .catch(jsonToImmutableError);
}

function getProfile(): Promise<Member> {
  const queryClient = getQueryClient();
  // NOTE: not all pages have this resource in context so hardcode it for now.
  return queryClient
    .fetchQuery(getProfileQuery())
    .then((data) => fromJS(data))
    .then(createMember)
    .catch(restApitoImmutableError);
}

function updateProfile(oldProfile: Member, newProfile: Member): Promise<Member> {
  const patch = createJsonPatch(oldProfile, newProfile, { propFilter: false });
  const queryClient = getQueryClient();

  return http
    .patch('/internal/profile', {
      body: JSON.stringify(patch),
    })
    .then(async (data) => {
      await queryClient.invalidateQueries({ queryKey: getProfileQuery.partialQueryKey() });
      return jsonToImmutable(data);
    })
    .then(createMember)
    .catch(jsonToImmutableError)
    .catch((error: ImmutableServerError) => {
      if (beastModeProfilePageRevamp()) {
        //  Return the error so that the caller can handle it for the new beast mode UI.
        throw error;
      } else {
        return beastModeError(error);
      }
    });
}

function updatePassword(password: PasswordUpdate): Promise<PasswordUpdate> {
  return http
    .post('/internal/profile/password', {
      body: JSON.stringify(password.toJS()),
    })
    .catch(jsonToImmutableError);
}

function resendVerification() {
  return http
    .post('/internal/profile/resend-verification')
    .catch(jsonToImmutableError)
    .then(beastModeRefresh, beastModeError);
}

function cancelVerification() {
  return http
    .post('/internal/profile/cancel-verification')
    .catch(jsonToImmutableError)
    .then(beastModeRefresh, beastModeError);
}

function enableMFA() {
  return http
    .post('/internal/profile/mfa/enable')
    .then(jsonToImmutable, jsonToImmutableError)
    .then(beastModeRefresh, beastModeError);
}

function confirmMFA(token: string, passcode: string): Promise<MFARecoveryCodes> {
  return http
    .post('/internal/profile/mfa/confirm', {
      body: { token, passcode },
    })
    .then(jsonToImmutable)
    .then(createMFARecoveryCodes)
    .catch(jsonToImmutableError);
}

function disableMFA() {
  return http.post('/internal/profile/mfa/disable').catch(jsonToImmutableError).then(beastModeRefresh, beastModeError);
}

function sendMFAEnableRequest(member: Member) {
  return http.post(member.getIn(['_links', 'sendMfaEnableRequest', 'href'])).catch(jsonToImmutableError);
}

function sendMFARecoveryCode(member: Member) {
  return http.post(member.getIn(['_links', 'sendMfaRecoveryCode', 'href'])).catch(jsonToImmutableError);
}

function resetMFA(member: Member) {
  return http.post(member.getIn(['_links', 'resetMemberMfa', 'href'])).catch(jsonToImmutableError);
}

function deleteAccountToken(account: Account): Promise<void> {
  return http.delete(`${account.selfLink()}/tokens/`).catch(jsonToImmutableError);
}

function turnOnTeamSync(): Promise<void> {
  return http.post('/internal/account/scim/managed-teams').catch(jsonToImmutableError);
}

function turnOffTeamSync(): Promise<void> {
  return http.delete('/internal/account/scim/managed-teams').catch(jsonToImmutableError);
}

function getSamlConfig(): Promise<SamlConfig> {
  return http
    .get('/internal/account/saml')
    .then(jsonToImmutable)
    .then(createSamlConfigRecord)
    .catch(jsonToImmutableError);
}

function getSamlAppDetails(): Promise<SamlAppDetails> {
  return http.get('/internal/account/saml-app-details').then(jsonToImmutable).catch(jsonToImmutableError);
}

function createSamlConfig(config: SamlConfig): Promise<SamlConfig> {
  return http
    .put('/internal/account/saml', {
      body: config,
    })
    .then(jsonToImmutable)
    .then(createSamlConfigRecord)
    .catch(jsonToImmutableError);
}

function updateSamlConfig(original: SamlConfig, modified: SamlConfig) {
  const patch = createJsonPatch(original.toRep(), modified.toRep());
  return http
    .patch('/internal/account/saml', {
      body: patch,
    })
    .catch(jsonToImmutableError);
}

function deleteSamlConfig(): Promise<void> {
  return http.delete('/internal/account/saml').catch(jsonToImmutableError);
}

function getScimConfig(): Promise<ScimConfig> {
  return http.get('/internal/account/scim').then(jsonToImmutable).then(createScimConfig).catch(jsonToImmutableError);
}

function deleteScimConfig(): Promise<void> {
  return http.delete('/internal/account/scim').catch(jsonToImmutableError);
}

function revokeAllSessions() {
  return http.post('/internal/account/revoke-sessions').catch(jsonToImmutableError);
}

function requestTeamInvite(joinTeamRequest: NewJoinTeamRequest) {
  return http
    .post('/internal/account/join', {
      body: JSON.stringify(joinTeamRequest.toJS()),
    })
    .catch(jsonToImmutableError);
}

const getFollowPreferences = () =>
  http
    .get('/internal/profile/following')
    .then(jsonToImmutable)
    .then(createFollowPreferences)
    .catch(jsonToImmutableError);

const updateFollowPreferences = (oldPreferences: FollowPreferences, nextPreferences: FollowPreferences) => {
  const patch = createJsonPatch(oldPreferences, nextPreferences, { shouldTestVersion: true });
  return http
    .patch(oldPreferences.selfLink(), {
      body: patch,
    })
    .then(jsonToImmutable)
    .then(createFollowPreferences)
    .catch(jsonToImmutableError);
};

const getNotificationSettings = (): Promise<ImmutableNotificationsSettingsType> =>
  http.get('/internal/profile/notification-settings').then(jsonToImmutable).catch(jsonToImmutableError);

const putNotificationSettings = (
  topic: NotificationTopics,
  channel: NotificationChannel,
  channelEnabled: boolean,
  oldSettings: NotificationsSettingsType,
) => {
  const body = {
    ...oldSettings,
    [topic]: {
      ...oldSettings[topic],
      [channel]: channelEnabled,
    },
    topic,
  };
  return http
    .put('/internal/profile/notification-settings', { body })
    .then(jsonToImmutable)
    .catch(jsonToImmutableError);
};

export const approveUnverifiedMemberRequest = (token: string): Promise<Member> =>
  http
    .put(`/internal/account/unverified-member/${token}`, { body: { role: 'reader' } })
    .then(jsonToImmutable)
    .then(createMember)
    .catch(jsonToImmutableError);

export const suggestInviteMembers = (emails: string[]): Promise<void> =>
  http.post('/internal/account/suggest-invites', { body: { emails } }).catch(jsonToImmutableError);

export {
  acceptInvitation,
  acceptInvitationMFA,
  addMemberToTeams,
  bulkAddAllMembersToTeams,
  bulkAddMembersToTeams,
  bulkReplaceAllMembersRoles,
  bulkReplaceMembersRoles,
  cancelVerification,
  confirmMFA,
  createAccountFromFacilitatedTrial,
  createUnverifiedMember,
  createSamlConfig,
  deleteAccount,
  deleteAccountToken,
  deleteMember,
  deleteSamlConfig,
  deleteScimConfig,
  disableMFA,
  enableMFA,
  getAccount,
  getFacilitatedTrial,
  getFollowPreferences,
  getMember,
  getNotificationSettings,
  getProfile,
  getSamlConfig,
  getSamlAppDetails,
  getScimConfig,
  inviteMembers,
  putNotificationSettings,
  reportAccesses,
  requestEnterpriseSeats,
  requestTeamInvite,
  resendInvite,
  resendVerification,
  resetMFA,
  revokeAllSessions,
  sendMFAEnableRequest,
  sendMFARecoveryCode,
  turnOffTeamSync,
  turnOnTeamSync,
  updateAccount,
  updateAccountOwner,
  updateFollowPreferences,
  updateMember,
  updatePassword,
  updateProfile,
  updateSamlConfig,
  deleteDuplicateOrganization,
  updateDuplicateOrganization,
};
